// IndexedDB storage for auth state (Service Worker compatible)

const DB_NAME = 'auth-db';
const STORE_NAME = 'auth-store';
const AUTH_KEY = 'auth';

const openDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        request.onupgradeneeded = () => {
            request.result.createObjectStore(STORE_NAME);
        };
    });
};

const idbGet = async <T>(key: string): Promise<T | null> => {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const request = tx.objectStore(STORE_NAME).get(key);
            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result ?? null);
        });
    } catch {
        return null;
    }
};

const idbSet = async <T>(key: string, value: T | null): Promise<void> => {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            value ? store.put(value, key) : store.delete(key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    } catch { /* ignore */ }
};

export const createAuthStorage = <T extends { accessToken: string | null }>(emptyState: T) => ({
    emptyState,

    load: async (): Promise<T> => {
        const state = await idbGet<T>(AUTH_KEY);
        return state ?? emptyState;
    },

    save: async (state: T): Promise<void> => {
        await idbSet(AUTH_KEY, state.accessToken ? state : null);
    },

    clear: async (): Promise<void> => {
        await idbSet(AUTH_KEY, null);
    }
});